import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-cpu';
import * as handpose from '@tensorflow-models/handpose';
import { detectGesture } from '../../engine/gestureDetector';
import { RefreshCw, Bug, X } from 'lucide-react';

export default function HandTracker({
  onCursorMove,
  onDwellTrigger,
  dwellDuration = 700,
  cooldownDuration = 1500,
  requiredGesture = 'PEACE_V',
  showCameraPreview = false,
  onCloseCameraPreview,
  isHandTrackingEnabled = true,
  onStatusChange,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const animFrameRef = useRef(null);
  const isInitializedRef = useRef(false);
  const activeStreamRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [activeFacingMode, setActiveFacingMode] = useState('user');
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    videoRes: '0x0',
    fps: 0,
    handsFound: 0,
    engine: 'TFJS Handpose AI',
    streamReady: false,
    backend: 'Initializing...',
  });

  const cursorRef = useRef({
    x: 0,
    y: 0,
    smoothX: 0,
    smoothY: 0,
    visible: false,
    gesture: 'NONE',
    gestureLabel: 'No Hand',
    gestureMatch: false,
  });

  const dwellStateRef = useRef({
    currentZone: null,
    startTime: 0,
    progress: 0,
    isCooldown: false,
    cooldownEndTime: 0,
  });



  const startCameraStream = useCallback(async (overrideDeviceId = null, facing = 'user') => {
    try {
      setErrorMsg(null);
      setCameraActive(false);

      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach(track => track.stop());
        activeStreamRef.current = null;
      }

      let constraints = { audio: false };

      if (overrideDeviceId) {
        constraints.video = { deviceId: { exact: overrideDeviceId } };
      } else {
        constraints.video = {
          facingMode: { ideal: facing },
          width: { ideal: 640 },
          height: { ideal: 480 },
        };
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (firstErr) {
        console.warn('First camera constraint failed, trying basic fallback:', firstErr);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing },
          audio: false,
        });
      }

      activeStreamRef.current = stream;

      if (videoRef.current) {
        const video = videoRef.current;
        video.muted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.srcObject = stream;

        try {
          await video.play();
        } catch (playErr) {
          console.warn('Initial video play error, unlocking on tap:', playErr);
        }

        setCameraActive(true);
        setDebugInfo(prev => ({ ...prev, streamReady: true }));
        if (onStatusChange) onStatusChange('Vision Tracking Active');
      }
    } catch (err) {
      console.error('Camera stream error:', err);
      setErrorMsg('Camera error: ' + err.message);
      setCameraActive(false);
      setDebugInfo(prev => ({ ...prev, streamReady: false }));
      if (onStatusChange) onStatusChange('Camera error');
    }
  }, [onStatusChange]);

  const toggleCameraFacing = useCallback(() => {
    const newFacing = activeFacingMode === 'user' ? 'environment' : 'user';
    setActiveFacingMode(newFacing);
    startCameraStream(null, newFacing);
  }, [activeFacingMode, startCameraStream]);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    let isMounted = true;

    async function initTFJSHandpose() {
      try {
        setIsLoading(true);
        if (onStatusChange) onStatusChange('Loading TFJS AI Vision Engine...');

        let activeBackend = 'webgl';
        try {
          await tf.setBackend('webgl');
          await tf.ready();
        } catch (webglErr) {
          console.warn('[TFJS Mobile] WebGL failed, falling back to CPU backend:', webglErr);
          activeBackend = 'cpu';
          await tf.setBackend('cpu');
          await tf.ready();
        }

        if (!isMounted) return;

        window.alert = (msg) => console.warn('[Suppressed Alert]:', msg);

        // Start camera stream only if hand tracking is enabled
        if (isHandTrackingEnabled) {
          startCameraStream(null, 'user');
        }

        const model = await handpose.load();

        if (!isMounted) return;

        modelRef.current = model;
        setDebugInfo(prev => ({ ...prev, backend: activeBackend.toUpperCase() }));
        setIsLoading(false);
        if (onStatusChange) onStatusChange(isHandTrackingEnabled ? 'Vision Ready' : 'Hand tracking off');

        if (isHandTrackingEnabled) {
          startDetectionLoop();
        }
      } catch (err) {
        console.error('[TFJS Mobile] Fatal initialization error:', err);
        if (isMounted) {
          setErrorMsg('AI Vision error: ' + err.message);
          setIsLoading(false);
          if (onStatusChange) onStatusChange('Vision error');
        }
      }
    }

    initTFJSHandpose();

    return () => {
      isMounted = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach(track => track.stop());
        activeStreamRef.current = null;
      }
    };
  }, []);

  // Dynamically start or stop webcam stream when isHandTrackingEnabled toggles
  useEffect(() => {
    if (!isHandTrackingEnabled) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach(track => track.stop());
        activeStreamRef.current = null;
      }
      setCameraActive(false);
      if (onCursorMove) {
        onCursorMove({ visible: false, gestureMatch: false });
      }
      if (onStatusChange) {
        onStatusChange('Hand tracking off');
      }
    } else if (isInitializedRef.current) {
      if (!activeStreamRef.current) {
        startCameraStream(null, activeFacingMode).then(() => {
          startDetectionLoop();
        });
      }
    }
  }, [isHandTrackingEnabled]);

  const startDetectionLoop = () => {
    let lastTime = performance.now();
    let frameCount = 0;
    let fpsTimer = performance.now();
    let isDetecting = false;

    const processFrame = async () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const model = modelRef.current;
      const now = performance.now();

      if (video && video.paused && video.readyState >= 2) {
        video.play().catch(e => console.warn('Auto resume video error:', e));
      }

      frameCount++;
      if (now - fpsTimer >= 1000) {
        setDebugInfo(prev => ({
          ...prev,
          fps: frameCount,
          videoRes: video ? `${video.videoWidth}x${video.videoHeight}` : '0x0',
        }));
        frameCount = 0;
        fpsTimer = now;
      }

      if (
        isHandTrackingEnabled &&
        video &&
        video.readyState >= 2 &&
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        model &&
        !isDetecting
      ) {
        try {
          isDetecting = true;

          const vw = video.videoWidth;
          const vh = video.videoHeight;

          const predictions = await model.estimateHands(video);
          const ctx = canvas ? canvas.getContext('2d') : null;

          const numHands = predictions ? predictions.length : 0;
          setDebugInfo(prev => ({ ...prev, handsFound: numHands }));

          if (canvas && ctx) {
            if (canvas.width !== vw || canvas.height !== vh) {
              canvas.width = vw;
              canvas.height = vh;
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }

          if (numHands > 0) {
            const pred = predictions[0];
            const landmarks = pred.landmarks.map(pt => ({
              x: pt[0] / vw,
              y: pt[1] / vh,
              z: pt[2] / vw,
            }));

            const gestureRes = detectGesture(landmarks);
            
            const isMatch =
              requiredGesture === 'ANY' ||
              gestureRes.gesture === requiredGesture;

            cursorRef.current.gesture = gestureRes.gesture;
            cursorRef.current.gestureLabel = gestureRes.label;
            cursorRef.current.gestureMatch = isMatch;

            const isSelfie = activeFacingMode === 'user';
            const rawX = (isSelfie ? (1 - gestureRes.cursorPoint.x) : gestureRes.cursorPoint.x) * window.innerWidth;
            const rawY = gestureRes.cursorPoint.y * window.innerHeight;

            const lerpFactor = 0.5;
            if (!cursorRef.current.visible) {
              cursorRef.current.smoothX = rawX;
              cursorRef.current.smoothY = rawY;
            } else {
              cursorRef.current.smoothX += (rawX - cursorRef.current.smoothX) * lerpFactor;
              cursorRef.current.smoothY += (rawY - cursorRef.current.smoothY) * lerpFactor;
            }
            cursorRef.current.x = rawX;
            cursorRef.current.y = rawY;
            cursorRef.current.visible = true;

            if (ctx) {
              drawPreviewSkeleton(ctx, landmarks, canvas.width, canvas.height, gestureRes.gesture, isSelfie);
            }

            handleDwellLogic(
              cursorRef.current.smoothX,
              cursorRef.current.smoothY,
              isMatch,
              gestureRes.gesture,
              now
            );
          } else {
            cursorRef.current.visible = false;
            cursorRef.current.gestureMatch = false;
            resetDwell();
          }
        } catch (err) {
          console.warn('[TFJS Handpose Exception]:', err);
        } finally {
          isDetecting = false;
        }
      }

      if (dwellStateRef.current.isCooldown) {
        const rem = Math.max(0, dwellStateRef.current.cooldownEndTime - now);
        if (rem <= 0) {
          dwellStateRef.current.isCooldown = false;
        }
      }

      if (onCursorMove) {
        onCursorMove({
          x: cursorRef.current.smoothX,
          y: cursorRef.current.smoothY,
          visible: cursorRef.current.visible,
          gesture: cursorRef.current.gesture,
          gestureLabel: cursorRef.current.gestureLabel,
          gestureMatch: cursorRef.current.gestureMatch,
          progress: dwellStateRef.current.progress,
          currentZone: dwellStateRef.current.currentZone,
          isCooldown: dwellStateRef.current.isCooldown,
          cooldownRemaining: Math.ceil(Math.max(0, dwellStateRef.current.cooldownEndTime - now) / 100) / 10,
        });
      }

      animFrameRef.current = requestAnimationFrame(processFrame);
    };

    animFrameRef.current = requestAnimationFrame(processFrame);
  };

  const handleDwellLogic = (x, y, isMatch, gesture, now) => {
    const dwell = dwellStateRef.current;

    if (dwell.isCooldown || !isMatch) {
      dwell.progress = 0;
      dwell.currentZone = null;
      return;
    }

    const elements = document.elementsFromPoint(x, y);
    let targetZone = null;

    for (const el of elements) {
      const zone = el.getAttribute('data-dwell-zone');
      if (zone) {
        targetZone = zone;
        break;
      }
    }

    if (!targetZone) {
      if (x < window.innerWidth * 0.5) {
        targetZone = 'TEAM_A_HALF';
      } else {
        targetZone = 'TEAM_B_HALF';
      }
    }

    if (dwell.currentZone !== targetZone) {
      dwell.currentZone = targetZone;
      dwell.startTime = now;
      dwell.progress = 0;
    } else {
      const elapsed = now - dwell.startTime;
      dwell.progress = Math.min(1.0, elapsed / dwellDuration);

      if (dwell.progress >= 1.0) {
        if (onDwellTrigger) {
          onDwellTrigger(targetZone);
        }

        dwell.isCooldown = true;
        dwell.cooldownEndTime = now + cooldownDuration;
        dwell.progress = 0;
        dwell.currentZone = null;
      }
    }
  };

  const resetDwell = () => {
    dwellStateRef.current.currentZone = null;
    dwellStateRef.current.progress = 0;
  };

  const drawPreviewSkeleton = (ctx, landmarks, width, height, detectedGesture, isSelfie) => {
    const isPeace = detectedGesture === 'PEACE_V';

    ctx.strokeStyle = isPeace ? '#c084fc' : '#06b6d4';
    ctx.lineWidth = isPeace ? 3 : 2;

    const connections = [
      [0, 1], [1, 2], [2, 3], [3, 4],
      [0, 5], [5, 6], [6, 7], [7, 8],
      [0, 9], [9, 10], [10, 11], [11, 12],
      [0, 13], [13, 14], [14, 15], [15, 16],
      [0, 17], [17, 18], [18, 19], [19, 20],
      [5, 9], [9, 13], [13, 17],
    ];

    connections.forEach(([i, j]) => {
      ctx.beginPath();
      const x1 = (isSelfie ? (1 - landmarks[i].x) : landmarks[i].x) * width;
      const y1 = landmarks[i].y * height;
      const x2 = (isSelfie ? (1 - landmarks[j].x) : landmarks[j].x) * width;
      const y2 = landmarks[j].y * height;
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });

    landmarks.forEach((lm, idx) => {
      ctx.beginPath();
      const cx = (isSelfie ? (1 - lm.x) : lm.x) * width;
      const cy = lm.y * height;

      if ((idx === 8 || idx === 12) && isPeace) {
        ctx.arc(cx, cy, 9, 0, 2 * Math.PI);
        ctx.fillStyle = '#c084fc';
        ctx.shadowColor = '#c084fc';
        ctx.shadowBlur = 12;
      } else {
        ctx.arc(cx, cy, 3, 0, 2 * Math.PI);
        ctx.fillStyle = '#22d3ee';
        ctx.shadowBlur = 0;
      }
      ctx.fill();
    });
  };

  return (
    <>
      <video
        ref={videoRef}
        className="hidden"
        playsInline
        muted
        autoPlay
      />

      {showCameraPreview && (
        <div className="fixed bottom-4 right-4 z-40 bg-slate-900/90 border border-slate-700/80 rounded-2xl p-2 shadow-2xl backdrop-blur-md flex flex-col gap-2 w-64 animate-fade-in">
          <div className="flex items-center justify-between px-1">
            <span className="text-[11px] font-black text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${cameraActive ? 'bg-emerald-400 animate-pulse' : 'bg-rose-500'}`} />
              AI Vision Feed
            </span>

            <div className="flex items-center gap-1">
              <button
                onClick={toggleCameraFacing}
                className="p-1 text-slate-400 hover:text-cyan-400 transition-colors"
                title="Switch Front/Back Camera"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowDebug(prev => !prev)}
                className={`p-1 transition-colors ${showDebug ? 'text-amber-400' : 'text-slate-400 hover:text-white'}`}
                title="Toggle Debug Stats"
              >
                <Bug className="w-3.5 h-3.5" />
              </button>
              {onCloseCameraPreview && (
                <button
                  onClick={onCloseCameraPreview}
                  className="p-1 text-slate-400 hover:text-rose-400 transition-colors"
                  title="Close Camera Window"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="relative w-full h-36 bg-black rounded-xl overflow-hidden border border-slate-800 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              className="w-full h-full object-cover transform scale-x-[-1]"
            />
            {isLoading && (
              <div className="absolute inset-0 bg-slate-950/80 flex flex-col items-center justify-center text-slate-300 text-xs gap-2">
                <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
                <span>Loading Vision Engine...</span>
              </div>
            )}
            {errorMsg && (
              <div className="absolute inset-0 bg-rose-950/90 text-rose-200 text-xs p-2 text-center flex items-center justify-center">
                {errorMsg}
              </div>
            )}
          </div>

          {showDebug && (
            <div className="bg-slate-950/90 rounded-lg p-2 text-[10px] font-mono text-slate-400 space-y-0.5 border border-slate-800">
              <div>Backend: <span className="text-cyan-400 font-bold">{debugInfo.backend}</span></div>
              <div>Res: <span className="text-slate-200">{debugInfo.videoRes}</span> | FPS: <span className="text-emerald-400 font-bold">{debugInfo.fps}</span></div>
              <div>Hands Found: <span className="text-amber-400 font-bold">{debugInfo.handsFound}</span></div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
